import { HandlerWithResult, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { BatchGetProductsApiParameter } from "@insite/client-framework/Services/ProductService";
import {
    chain,
    ProcessProducts,
} from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import {
    OrderUploadRowError,
    UploadedItem,
} from "@insite/client-framework/Store/Components/OrderUpload/OrderUploadState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import { getUnitRegularPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";

const UploadError = {
    None: "None",
    NotEnough: "NotEnough",
    ConfigurableProduct: "ConfigurableProduct",
    StyledProduct: "StyledProduct",
    Unavailable: "Unavailable",
    InvalidUnit: "InvalidUnit",
    NotFound: "NotFound",
    OutOfStock: "OutOfStock",
} as const;
const extendedUploadError = {
    ZeroPricedProduct: "ZeroPricedProduct",
} as const;

export const AllUploadError = { ...UploadError, ...extendedUploadError } as const;

type ValueOf<T> = T[keyof T];
export type AllUploadErrorType = ValueOf<typeof AllUploadError>;

export interface BatchLoadProductsParameter extends BatchGetProductsApiParameter {
    firstRowHeading: boolean;
    checkInventory: boolean;
}

export interface BatchLoadProductsResult {
    apiParameter: BatchGetProductsApiParameter;
    apiResult: (ProductDto | null)[];
    products: ProductDto[];
    rowErrors: OrderUploadRowErrorType[];
}

type HandlerType = HandlerWithResult<BatchLoadProductsParameter, BatchLoadProductsResult>;

const validateProductExtended = (product: ProductDto, checkInventory: boolean) => {
    if (product.canConfigure || (product.isConfigured && !product.isFixedConfiguration)) {
        return AllUploadError.ConfigurableProduct;
    }

    if (product.isStyleProductParent) {
        return AllUploadError.StyledProduct;
    }

    if (checkInventory && product.qtyOnHand === 0 && product.trackInventory && !product.canBackOrder) {
        return AllUploadError.OutOfStock;
    }

    if (!product.canAddToCart) {
        return AllUploadError.Unavailable;
    }

    if (
        product.pricing &&
        getUnitRegularPrice(product.pricing, product.qtyOrdered).price < 1 &&
        product.quoteRequired === false
    ) {
        return AllUploadError.ZeroPricedProduct;
    }

    return AllUploadError.None;
};

const setProductUnitOfMeasure = (
    product: ProductDto,
    item: UploadedItem,
    index: number,
    rowErrors: OrderUploadRowErrorType[],
) => {
    const uoms = getProductUnitOfMeasures(product, item);
    if (uoms.length > 0) {
        const um = uoms[0];
        product.selectedUnitOfMeasure = um.unitOfMeasure;
        product.selectedUnitOfMeasureDisplay = um.unitOfMeasureDisplay;
        product.unitOfMeasure = um.unitOfMeasure;
        product.unitOfMeasureDisplay = um.unitOfMeasureDisplay;

        return false;
    }

    if (item.unitOfMeasure) {
        const errorProduct = mapRowError(index, AllUploadError.InvalidUnit, item.name, product);
        errorProduct.umRequested = item.unitOfMeasure;
        rowErrors.push(errorProduct);

        return true;
    }

    return false;
};

const addProductToList = (
    product: ProductDto,
    item: UploadedItem,
    index: number,
    result: BatchLoadProductsResult,
    checkInventory: boolean,
) => {
    const baseUnitOfMeasure = getBaseUnitOfMeasure(product);
    const currentUnitOfMeasure = getCurrentUnitOfMeasure(product);
    if (
        checkInventory &&
        product.trackInventory &&
        !product.canBackOrder &&
        !product.quoteRequired &&
        baseUnitOfMeasure &&
        currentUnitOfMeasure &&
        product.qtyOrdered * baseUnitOfMeasure.qtyPerBaseUnitOfMeasure >
            product.qtyOnHand * currentUnitOfMeasure.qtyPerBaseUnitOfMeasure
    ) {
        const rowError = mapRowError(index, AllUploadError.NotEnough, item.name, product);
        result.rowErrors.push(rowError);
    }

    result.products.push(product);
};

const getBaseUnitOfMeasure = (product: ProductDto) => {
    const defaultUnitOfMeasure = getDefaultProductUnitOfMeasureDto();
    let baseUnitOfMeasure = product.productUnitOfMeasures?.filter(u => u.isDefault)[0];
    if (!baseUnitOfMeasure) {
        baseUnitOfMeasure = defaultUnitOfMeasure;
    }

    return baseUnitOfMeasure;
};

const getCurrentUnitOfMeasure = (product: ProductDto) => {
    const defaultUnitOfMeasure = getDefaultProductUnitOfMeasureDto();
    let currentUnitOfMeasure = product.productUnitOfMeasures?.filter(u => u.unitOfMeasure === product.unitOfMeasure)[0];
    if (!currentUnitOfMeasure) {
        currentUnitOfMeasure = defaultUnitOfMeasure;
    }

    return currentUnitOfMeasure;
};

const getDefaultProductUnitOfMeasureDto = () => {
    return {
        productUnitOfMeasureId: "",
        unitOfMeasure: "",
        unitOfMeasureDisplay: "",
        description: "",
        qtyPerBaseUnitOfMeasure: 1,
        roundingRule: "",
        isDefault: false,
        availability: null,
    };
};

export interface OrderUploadRowErrorType extends Omit<OrderUploadRowError, "error"> {
    error: AllUploadErrorType;
}

const mapRowError = (
    index: number,
    error: AllUploadErrorType,
    name: string,
    product: ProductDto,
): OrderUploadRowErrorType => {
    return {
        index,
        error,
        name,
        qtyRequested: product.qtyOrdered,
        umRequested: product.unitOfMeasureDisplay,
        qtyOnHand: product.qtyOnHand,
    };
};

const getProductUnitOfMeasures = (product: ProductDto, item: UploadedItem) => {
    const lowerCaseItemUm = item.unitOfMeasure ? item.unitOfMeasure.toLowerCase() : "";

    return lowerCaseItemUm && product.productUnitOfMeasures
        ? product.productUnitOfMeasures.filter(
              u =>
                  u.unitOfMeasure.toLowerCase() === lowerCaseItemUm ||
                  u.unitOfMeasureDisplay.toLowerCase() === lowerCaseItemUm ||
                  u.description.toLowerCase() === lowerCaseItemUm,
          )
        : [];
};

replaceInChain(chain, ProcessProducts, props => {
    const state = props.getState();
    const currentPage = getCurrentPage(state);
    const processState =
        ["VmiBinsPage", "VmiLocationDetailsPage"].indexOf(currentPage.type) !== -1
            ? state.components.vmiBinsImportModal
            : state.components.orderUpload;
    const products = props.result.apiResult;
    if ("uploadCancelled" in processState && processState.uploadCancelled) {
        return false;
    }

    for (let i = 0; i < products.length; i++) {
        const item =
            "uploadedItems" in processState
                ? processState.uploadedItems[i]
                : "parsedItems" in processState
                ? processState.parsedItems[i]
                : null;

        if (item === null) {
            continue;
        }

        const index = props.parameter.firstRowHeading ? i + 2 : i + 1;
        const product = products[i];
        const propsResultError = props.result.rowErrors as unknown as OrderUploadRowErrorType[];
        if (product) {
            const error = validateProductExtended(product, props.parameter.checkInventory);
            if (error === AllUploadError.None) {
                product.qtyOrdered = !item.qtyOrdered ? 1 : item.qtyOrdered;
                const isInvalidUnitOfMeasure = setProductUnitOfMeasure(product, item, index, propsResultError);
                if (!isInvalidUnitOfMeasure) {
                    addProductToList(
                        product,
                        item,
                        index,
                        props.result as unknown as BatchLoadProductsResult,
                        props.parameter.checkInventory,
                    );
                }
            } else if (error === AllUploadError.ZeroPricedProduct) {
                propsResultError.push(
                    mapRowError(index, AllUploadError.ZeroPricedProduct, item.name, {
                        qtyOrdered: !item.qtyOrdered ? 1 : item.qtyOrdered,
                        unitOfMeasureDisplay: item.unitOfMeasure,
                    } as ProductDto),
                );
            } else {
                propsResultError.push(mapRowError(index, error, item.name, product));
            }
        } else {
            propsResultError.push(
                mapRowError(index, AllUploadError.NotFound, item.name, {
                    qtyOrdered: item.qtyOrdered,
                    unitOfMeasureDisplay: item.unitOfMeasure,
                } as ProductDto),
            );
        }
    }
});
